import { createTheme, Theme, ThemeOptions } from '@mui/material'
import { SimplePaletteColorOptions, TypeBackground } from '@mui/material/styles/createPalette'
import { TypographyOptions } from '@mui/material/styles/createTypography'
import { deDE, enUS } from '@mui/material/locale'
import { Customer } from '../shared/types/commonTypes'
import FsMeWebBold from 'url:../shared/assets/fonts/FSMeWeb-Bold.woff'
import FsMeWebRegular from 'url:../shared/assets/fonts/FSMeWeb-Regular.woff'

const getAppTheme = (language: string, localCustomer?: Customer): Theme => {
  const customer = localCustomer || Customer.default
  const defaultTheme: ThemeOptions | undefined = {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1300,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: [
        'Roboto Condensed',
        'Roboto',
        '"Helvetica"',
        'Arial',
        'sans-serif',
      ].join(','),
      subtitle1: {
        fontFamily: 'Roboto Condensed',
        textTransform: 'uppercase',
        fontWeight: 'inherit',
        fontSize: 16,
      },
    },
    palette: {
      primary: {
        main: '#646464',
        contrastText: '#fff',
      },
      secondary: {
        main: '#E99425',
      },
      info: {
        main: '#646464',
      },
      background: {
        default: '#ebebeb',
        paper: '#ffffff',
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: 'Roboto Condensed',
          },
          body1: {
            fontFamily: 'Roboto Condensed',
            fontSize: '16px',
          },
          body2: {
            fontFamily: 'Roboto Condensed',
            fontSize: '14px',
          },
          h2: {
            fontFamily: 'Roboto Condensed',
            fontWeight: 700,
            fontSize: '1.5em',
          },
          h3: {
            fontFamily: 'Roboto Condensed',
            fontSize: '18px',
          },
          h4: {
            fontFamily: 'Roboto Condensed',
            fontSize: '1em',
            fontWeight: 700,
          },
          h5: {
            fontFamily: 'Roboto Condensed',
            fontSize: '14px',
            fontWeight: 700,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: '1px solid #b5b5b5',
            background: 'transparent',
            width: '100%',
            height: 'fit-content',
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            color: '#646464',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'initial',
          },
          text: {
            textTransform: 'initial',
          },
          outlined: {
            textTransform: 'initial',
          },
          contained: {
            textTransform: 'initial',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: '#707070',
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginTop: 0,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
        },
      },
    },
  }

  const palettePrimary = defaultTheme?.palette?.primary as SimplePaletteColorOptions
  const paletteSecondary = defaultTheme?.palette?.secondary as SimplePaletteColorOptions
  const paper = defaultTheme?.palette?.background as Partial<TypeBackground>
  const typography = defaultTheme?.typography as TypographyOptions

  if (customer === Customer.default) {
    palettePrimary.main = '#014284'
    paletteSecondary.main = '#ffb74d'
    if (defaultTheme?.palette?.background) defaultTheme.palette.background.default = '#f5f5f5'
    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiButton && defaultTheme.components.MuiButton.styleOverrides) {
      defaultTheme.components.MuiButton.styleOverrides = {
        textPrimary: {
          color: '#014284',
          border: 0,
          '&:hover': {
            color: '#ffb74d',
            border: 0,
          },
        },
        textSecondary: {
          color: '#ffb74d',
          border: 0,
          '&:hover': {
            color: '#014284',
            border: 0,
          },
        },
        containedPrimary: {
          color: '#FFFFFF',
          backgroundColor: '#014284',
        },
        containedSecondary: {
          color: '#FFFFFF',
          backgroundColor: '#ffb74d',
        },
      }
    }
  }

  if (customer === Customer.allianz) {
    palettePrimary.main = '#075994'
  }

  if (customer === Customer.barmenia) {
    palettePrimary.main = '#00ADEF'
  }

  if (customer === Customer.concordia) {
    palettePrimary.main = '#003399'
  }

  if (customer === Customer.dvag) {
    palettePrimary.main = '#a7893d'
    paletteSecondary.main = '#424242'
    typography.subtitle1 = {
      fontFamily: '\'Roboto\', sans-serif',
      textTransform: 'uppercase',
      fontWeight: '700',
      fontSize: 17,
    }
    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiButton && defaultTheme.components.MuiButton.styleOverrides) {
      defaultTheme.components.MuiButton.styleOverrides = {
        text: {
          minWidth: 0,
          backgroundColor: 'transparent',
          border: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            border: 0,
          },
        },
        containedPrimary: {
          color: '#FFFFFF',
          backgroundColor: '#a7893d',
          border: '1px solid #a7893d',
          '&:hover': {
            color: '#a7893d',
            backgroundColor: '#FFFFFF',
            border: '1px solid #a7893d',
          },
        },
        containedSecondary: {
          color: '#FFFFFF',
          backgroundColor: '#424242',
          border: '1px solid #424242',
          '&:hover': {
            color: '#424242',
            backgroundColor: '#FFFFFF',
            border: '1px solid #424242',
          },
        },
      }
    }
  }

  if (customer === Customer.ergo) {
    palettePrimary.main = '#8e0038'

    typography.subtitle1 = {
      fontFamily: 'FSMeWeb',
      textTransform: 'none',
      fontWeight: '400',
      fontSize: 14,
    }

    typography.fontFamily = [
      'FSMeWeb',
      'Roboto Condensed',
      'Roboto',
      '"Helvetica"',
      'Arial',
      'sans-serif',
    ].join(',')
    if (defaultTheme?.palette?.background) defaultTheme.palette.background.default = '#f7dcbb'

    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiTypography && defaultTheme.components.MuiTypography.styleOverrides) {
      defaultTheme.components.MuiTypography.styleOverrides = {
        body1: {
          fontFamily: 'FSMeWeb',
        },
        body2: {
          fontFamily: 'FSMeWeb',
          fontSize: '14px',
        },
        h2: {
          fontFamily: 'FSMeWeb',
          fontWeight: 700,
          fontSize: '1.5em',
        },
        h3: {
          fontFamily: 'FSMeWeb',
          fontSize: '18px',
        },
        h4: {
          fontFamily: 'FSMeWeb',
          fontSize: '1em',
          fontWeight: 700,
        },
        h5: {
          fontFamily: 'FSMeWeb',
          fontSize: '14px',
          fontWeight: 700,
        },
      }
    }

    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiCssBaseline && defaultTheme.components.MuiCssBaseline.styleOverrides) {
      defaultTheme.components.MuiCssBaseline.styleOverrides = `
        @font-face {
          font-family: 'FSMeWeb';
          font-style: normal;
          font-weight: 600;
          src: local('FSMeWeb'), local('FSMeWeb-Bold'), url(${FsMeWebBold}) format('woff');
        }
        @font-face {
          font-family: 'FSMeWeb';
          font-style: normal;
          font-weight: 400;
          src: local('FSMeWeb'), local('FSMeWeb-Regular'), url(${FsMeWebRegular}) format('woff');
        }
      `
    }

    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiButton && defaultTheme.components.MuiButton.styleOverrides) {
      defaultTheme.components.MuiButton.styleOverrides = {
        ...defaultTheme.components.MuiButton.styleOverrides,
        root: {
          // minWidth: 0,
          // backgroundColor: 'transparent',
          // border: 0,
          // '&:hover': {
          //   backgroundColor: 'transparent',
          //   border: 0,
          // },
          // textTransform: 'initial',
        },
        textPrimary: {
          color: '#8e0038',
          '&:hover': {
            color: '#807e6f',
          },
          textTransform: 'initial',
        },
        textSecondary: {
          color: '#807e6f',
          '&:hover': {
            color: '#8e0038',
          },
          textTransform: 'initial',
        },
        containedPrimary: {
          color: '#FFFFFF',
          backgroundColor: '#8e0038',
          border: '1px solid #8e0038',
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: '#807e6f',
            border: '1px solid #807e6f',
          },
          textTransform: 'initial',
        },
        containedSecondary: {
          color: '#FFFFFF',
          backgroundColor: '#807e6f',
          border: '1px solid #807e6f',
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: '#8e0038',
            border: '1px solid #8e0038',
          },
          textTransform: 'initial',
        },
      }
    }
  }

  if (customer === Customer.generali) {
    palettePrimary.main = '#c5281c'
  }

  if (customer === Customer.huk) {
    palettePrimary.main = '#f4c71d'
    palettePrimary.contrastText = '#4f4f51'
    paletteSecondary.main = '#4f4f51'
    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiButton && defaultTheme.components.MuiButton.styleOverrides) {
      defaultTheme.components.MuiButton.styleOverrides = {
        ...defaultTheme.components.MuiButton.styleOverrides,
        root: {
          '&.Mui-disabled': {
            border: 'transparent',
          },
        },
        text: {
          minWidth: 0,
          backgroundColor: 'transparent',
          border: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            border: 0,
          },
          textTransform: 'initial',
        },
        contained: {
          textTransform: 'uppercase',
        },
        outlined: {
          textTransform: 'uppercase',
        },
        containedPrimary: {
          background: palettePrimary.main,
          color: paletteSecondary.main,
          border: `1px solid ${palettePrimary.main}`,
          '&:hover': {
            background: paletteSecondary.main,
            color: palettePrimary.main,
            border: `1px solid ${palettePrimary.main}`,
          },
        },
        containedSecondary: {
          background: paletteSecondary.main,
          color: palettePrimary.main,
          border: `1px solid ${palettePrimary.main}`,
          '&:hover': {
            background: palettePrimary.main,
            color: paletteSecondary.main,
            border: `1px solid ${palettePrimary.main}`,
          },
        },
      }
    }
  }

  if (customer === Customer.localyzer) {
    palettePrimary.main = '#00838f'
  }

  if (customer === Customer.marcapo) {
    palettePrimary.main = '#FFBB00'
    paletteSecondary.main = '#111111'
  }

  if (customer === Customer.nuernberger) {
    palettePrimary.main = '#003273'
  }

  if (customer === Customer.ovb) {
    palettePrimary.main = '#003a65'
    paletteSecondary.main = '#003a65'
  }

  if (customer === Customer.provinzial) {
    palettePrimary.main = '#00884d'
    paletteSecondary.main = '#ffd200'
  }

  if (customer === Customer.vkb) {
    palettePrimary.main = '#004f9f'
  }

  if (customer === Customer.vrk) {
    palettePrimary.main = '#006cb7'
  }

  if (customer === Customer.wuestenrotAT) {
    palettePrimary.main = '#e61415'
  }

  if (customer === Customer.wuw) {
    palettePrimary.main = '#e63f0c'
  }

  if (customer === Customer.emasters) {
    palettePrimary.main = '#ff001a'
  }

  if (customer === Customer.sanacorp) {
    palettePrimary.main = '#98bf0c'
  }

  if (customer === Customer.sympany) {
    palettePrimary.main = '#9185be'
    paletteSecondary.main = '#891350'
    paper.default = '#f3e7da'
    if (defaultTheme && defaultTheme.components && defaultTheme.components.MuiButton && defaultTheme.components.MuiButton.styleOverrides) {
      defaultTheme.components.MuiButton.styleOverrides = {
        ...defaultTheme.components.MuiButton.styleOverrides,
        root: {
          '&.Mui-disabled': {
            border: 'transparent',
          },
        },
        text: {
          minWidth: 0,
          backgroundColor: 'transparent',
          border: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            border: 0,
          },
          textTransform: 'initial',
        },
        outlined: {
          color: palettePrimary.main,
          borderColor: palettePrimary.main,
          '&:hover': {
            color: paletteSecondary.main,
            borderColor: paletteSecondary.main,
          },
          '&:active': {
            color: '#ed1b2f',
            borderColor: '#ed1b2f',
          },
        },
        contained: {
          textTransform: 'initial',
          background: palettePrimary.main,
          '&:hover': {
            background: paletteSecondary.main,
          },
          '&:active': {
            background: '#ed1b2f',
          },
        },
      }
    }
  }

  const theme: Theme = createTheme(defaultTheme, language === 'de' ? deDE : enUS)
  return theme
}
export default getAppTheme
